<template>
  <div
    ref="scrollContainerRef"
    v-paywall="paywallOptions"
    class="events-container"
    data-test-id="events-container"
    :class="paywallOptions.showPaywall ? 'max-h-[600px] bg-surface-light border border-surface-dark rounded-md px-4 overflow-hidden relative' : ''"
  >
    <div class="flex flex-row items-center justify-between events-controls">
      <div>
        <AM3InputText
          v-model="searchQuery"
          aria-label="Search events"
          placeholder="Search events"
          class="search-input"
          data-test-id="customer-events-search"
        >
          <template #left>
            <AM3Icon
              type="magnifying-glass"
              size="md"
            />
          </template>
        </AM3InputText>
      </div>
      <div class="w-full basis-56">
        <AM3Selectbox
          v-model="sortOption"
          placeholder="Sort events"
          :options="sortOptions"
          :display-value="displaySortValue"
          data-test-id="customer-events-sort"
          @change="handleSortChange"
        >
          <template #after-options>
            <AM3DateRangeFilter
              v-model:start-date="startDate"
              v-model:end-date="endDate"
              @apply="handleDateFilter"
              @clear="clearDateFilter"
            />
          </template>
        </AM3Selectbox>
      </div>
    </div>
    <div
      v-if="computedEvents.length >= 1"
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 my-6 "
    >
      <AM3EventCard
        v-for="event in computedEvents"
        :key="event.oid"
        :event="event"
        :enable-dropdown="false"
      />
    </div>

    <div
      v-else
      data-test-id="customer-events-empty-state"
      class="[&>div]:mx-auto [&>div]:space-y-8 my-24"
    >
      <div
        v-if="isFetchingEvents"
        class="flex flex-col items-center justify-center"
      >
        <AM3Loading
          size="lg"
          color="brand"
        />
        <div class="mt-4 text-center text-lg font-medium">
          Loading events...
        </div>
      </div>
      <AM3EmptyState
        v-else
        :heading="emptyStateContent.heading"
        :description="emptyStateContent.description"
        class="events-empty-state"
      />
    </div>
  </div>
</template>

<!-- Note: AM3EventsList.vue is visually similar but intentionally separate. This component sorts
     client-side using customerModule.fanEvents + purchaseDatePerEvent, while AM3EventsList sorts
     server-side via API params. Shared logic lives in useEventDateFilter and AM3EventCard. -->
<script lang="ts" setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, watchEffect } from 'vue'
import { useStore } from 'vuex'
import accounting from 'accounting'
import debounce from 'debounce'

import AM3EventCard from '@/components/am3/event-card/AM3EventCard.vue'
import AM3EmptyState from '@/components/am3/empty-state/AM3EmptyState.vue'
import AM3Loading from '@/components/am3-generic/base/AM3Loading.vue'
import AM3Selectbox from '@/components/am3-generic/inputs/selection/AM3Selectbox.vue'
import AM3DateRangeFilter from '@/components/am3-generic/inputs/selection/AM3DateRangeFilter.vue'

import { dummyFanEvents } from '@/utils/audience'
import type { Event } from '@/types/resources/event'
import type { RootState } from '@/store/modules/types'
import { useInfiniteScrollComposable } from '@/composables/useInfiniteScroll'
import { useEventDateFilter } from '@/composables/useEventDateFilter'
import { useCurrencySymbol } from '@/composables/useCurrencySymbol'

// Refs
const searchQuery = ref<string | null>(null)
const sortOption = ref<string>('purchase-date')
const scrollContainerRef = ref<HTMLElement | null>(null)

const sortOptions = [
  { label: 'Purchase date', value: 'purchase-date' },
  { label: 'Recently created', value: 'recently-created' },
  { label: 'Recently edited', value: 'sysMtime desc' },
  { label: 'Event date (Most recent)', value: 'event-date-most-recent' },
  { label: 'Event date (Oldest)', value: 'event-date-oldest' },
  { label: 'Title A-Z', value: 'title-a-z' },
  { label: 'Title Z-A', value: 'title-z-a' },
]

const store = useStore<RootState>()
const currencySymbol = useCurrencySymbol()

// Date filter composable
const {
  startDate,
  endDate,
  hasDateFilter,
  handleDateFilter,
  clearDateFilter,
} = useEventDateFilter(
  () => {
    store.commit('customerModule/RESET_CUSTOMER_EVENTS')
    fetchEvents()
  },
  () => {
    store.commit('customerModule/RESET_CUSTOMER_EVENTS')
    fetchEvents()
  },
)

// Vuex access
const customer = computed(() => store.state.customerModule.customer)
const customerState = computed(() => store.state.customerModule)
const isFetchingEvents = computed(() => store.state.customerModule.isFetchingEvents)
const isFeatureEnabled = computed(() => store.getters['authModule/isFeatureEnabled'])
// const getEventTotalTicketSold = store.getters['eventModule/getEventTotalTicketSold']
const getEventAttendees = store.getters['eventModule/getEventAttendees']

// Derived
const isCustomerProfileFeatureEnabled = computed(() =>
  isFeatureEnabled.value(['customer-profiles']),
)

const paywallOptions = computed(() => ({
  showPaywall: !isCustomerProfileFeatureEnabled.value,
  title: 'Unlock event data',
  description: 'View events your contacts have attended and track engagement.',
  buttonText: 'Upgrade Plan',
  buttonIcon: 'rocket',
  linkDestination: '/upgrade',
}))

const hasMoreEvents = computed(() => {
  if (!customer.value?.events) return false
  return customerState.value.actionedEventOids.length < customer.value.events.length
})

const displaySortValue = computed(() => {
  if (hasDateFilter.value) {
    return 'Custom date'
  }
  return sortOptions.find(opt => opt.value === sortOption.value)?.label || 'Sort events'
})

// Check if any filters are active
const hasActiveFilters = computed(() => {
  return !!(
    searchQuery.value
    || startDate.value
    || endDate.value
  )
})

// Dynamic empty state content based on filter state
const emptyStateContent = computed(() => {
  if (hasActiveFilters.value) {
    return {
      heading: 'No events found matching your criteria',
      description: 'Try adjusting your search or date filters to find what you\'re looking for.',
    }
  }
  return {
    heading: `${customer.value?.firstName || 'This contact'} has not purchased any tickets yet`,
    description: 'To view ticket history, sync or import data and check back later.',
  }
})

const sortedEvents = computed<Event[]>(() => {
  const events = [...(customerState.value.fanEvents || [])]

  switch (sortOption.value) {
    case 'purchase-date': {
      const purchaseDates = customer.value?.purchaseDatePerEvent || {}
      return events.sort((a, b) => {
        const dateA = purchaseDates[a.oid] ? new Date(purchaseDates[a.oid]).getTime() : 0
        const dateB = purchaseDates[b.oid] ? new Date(purchaseDates[b.oid]).getTime() : 0
        return dateB - dateA
      })
    }
    case 'recently-created':
      return events.sort((a, b) => new Date(b.sysCtime).getTime() - new Date(a.sysCtime).getTime())
    case 'event-date-most-recent':
      return events.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
    case 'event-date-oldest':
      return events.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
    case 'title-a-z':
      return events.sort((a, b) => a.name.localeCompare(b.name))
    case 'title-z-a':
      return events.sort((a, b) => b.name.localeCompare(a.name))
    default:
      return events
  }
})

const computedEvents = computed((): Event[] => {
  if (!isCustomerProfileFeatureEnabled.value) return dummyFanEvents()

  return sortedEvents.value.map((event): Event => {
    const oid = event.oid
    const sales = customerState.value.customer?.ticketSalesPerEvent?.[oid] || 0
    const quantity = customerState.value.customer?.ticketsPerEvent?.[oid] || 0

    return {
      ...event,
      ticketStats: [
        {
          attendees: quantity,
          oid: getEventAttendees(event),
          totalTicketSales: sales,
          totalTicketsSold: quantity,
          totalTicketSalesFormatted: accounting.formatMoney(sales / 100, currencySymbol.value),
          totalTicketsSoldFormatted: accounting.formatNumber(quantity),
        },
      ],
    }
  })
})

// Debounced fetch
const fetchEvents = (append = false) => {
  // Guard: Don't fetch if already fetching
  if (isFetchingEvents.value) return

  // Guard: Don't fetch more if we've already loaded all events
  if (append && !hasMoreEvents.value) return

  const searchString = searchQuery.value
  const dateRange = hasDateFilter.value
    ? {
        startDate: startDate.value ? new Date(startDate.value) : null,
        endDate: endDate.value ? new Date(endDate.value) : null,
      }
    : null

  store.dispatch('customerModule/FETCH_CUSTOMER_EVENTS', { searchString, append, dateRange })
}

// Debounced search on query change
const debouncedSearch = debounce(() => {
  store.commit('customerModule/RESET_CUSTOMER_EVENTS')
  fetchEvents()
}, 250)

// Watch search query for auto-search
watch(searchQuery, (newVal, oldVal) => {
  // If search is cleared, refresh immediately
  if (newVal === '' || newVal === null) {
    store.commit('customerModule/RESET_CUSTOMER_EVENTS')
    fetchEvents()
  }
  // If user is typing, debounce the search
  else if (newVal !== oldVal && newVal) {
    debouncedSearch()
  }
})

// Initial fetch
watch(customer, (val, oldVal) => {
  if (val?.oid && !oldVal && !isFetchingEvents.value && isCustomerProfileFeatureEnabled.value) {
    fetchEvents()
  }
}, { immediate: true })

// Reset events on mount/unmount
onMounted(() => {
  store.commit('customerModule/RESET_CUSTOMER_EVENTS')
})
onBeforeUnmount(() => {
  store.commit('customerModule/RESET_CUSTOMER_EVENTS')
})

// Infinite scroll
useInfiniteScrollComposable(() => fetchEvents(true), {
  distance: 200,
  debounce: 300,
})

// Methods
const handleSortChange = () => {
  store.commit('customerModule/RESET_CUSTOMER_EVENTS')
  fetchEvents()
}
</script>
